import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Plus, Edit, Trash, AlertTriangle, Settings, Eye } from 'lucide-react';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { Badge } from '../ui/Badge';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { IMEIManagementModal } from '../modals/IMEIManagementModal';

export const ChargesTab = () => {
    const { charges, addCharge, updateCharge, deleteCharge, activeCharge, setActiveCharge, statuses, canEditContent, productData } = usePIM();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isIMEIModalOpen, setIsIMEIModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [editingCharge, setEditingCharge] = useState(null);
    const [chargeToDelete, setChargeToDelete] = useState(null);
    const [error, setError] = useState('');

    // Sorting
    const { items: sortedCharges, requestSort, sortConfig } = useSortableData(charges);

    const [formData, setFormData] = useState({
        id: '', date: '', order: '', sw: '',
        status: statuses.length > 0 ? statuses[0].name : '',
        comment: '',
        serialStart: '', serialEnd: '', imeiStart: '', imeiEnd: '',
        deliveryDate: '', shippingDate: '', quantity: '', uom: '', unitPrice: '', amount: '',
        customsCode: '', inspectionDate: ''
    });

    // UX: Calculate Informational Parent (latest by created_at or ID)
    const informationalParent = React.useMemo(() => {
        if (!charges || charges.length === 0) return null;
        // Sort by ID descending as proxy for creation time if no created_at, or assume charges is already sorted?
        // Let's assume passed 'charges' might be in any order, so we sort.
        // Ideally use created_at, but ID is a safe fallback for "latest".
        return [...charges].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
    }, [charges]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError(''); // Clear error on edit
    };

    const validateIMEI = () => {
        const newStart = parseInt(formData.imeiStart);
        const newEnd = parseInt(formData.imeiEnd);

        if (isNaN(newStart) || isNaN(newEnd)) return true; // Skip if empty/invalid

        // Simple check: Start must be <= End
        if (newStart > newEnd) {
            setError('IMEI Start cannot be greater than IMEI End.');
            return false;
        }

        // Overlap Check (Skip current charge if editing)
        const hasOverlap = charges.some(c => {
            if (editingCharge && c.id === editingCharge.id) return false; // Skip self

            const existingStart = parseInt(c.imei_start || c.imeiStart);
            const existingEnd = parseInt(c.imei_end || c.imeiEnd);
            if (isNaN(existingStart) || isNaN(existingEnd)) return false;

            return (newStart <= existingEnd) && (newEnd >= existingStart);
        });

        if (hasOverlap) {
            setError('CRITICAL: IMEI Range overlaps with an existing charge!');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!formData.order) {
            setError('Order Number is required.');
            return;
        }
        // ID is required for new charges, but might be read-only for edits depending on requirements.
        // Assuming ID is editable or at least required.
        if (!formData.id) {
            setError('Charge ID is required.');
            return;
        }

        if (!validateIMEI()) return;

        const chargeData = {
            charge_id: formData.id,
            production_date: formData.date,
            order_number: formData.order,
            software_version: formData.sw,
            serial_start: formData.serialStart,
            serial_end: formData.serialEnd,
            imei_start: formData.imeiStart,
            imei_end: formData.imeiEnd,
            status: formData.status, // Send "" to inherit
            comments: formData.comment,
            delivery_date: formData.deliveryDate,
            shipping_date: formData.shippingDate,
            quantity: formData.quantity,
            uom: formData.uom,
            unit_price: formData.unitPrice,
            amount: formData.amount,
            customs_code: formData.customsCode,
            inspection_date: formData.inspectionDate
        };

        if (editingCharge) {
            await updateCharge(editingCharge.id, chargeData);
        } else {
            // Pass activeCharge.id to copy data from the currently active charge
            await addCharge(chargeData, activeCharge?.id);
        }

        setIsModalOpen(false);
        resetForm();
    };

    const resetForm = () => {
        setViewMode(false);
        setFormData({
            id: '', date: '', order: '', sw: '',
            status: '', // Default to empty for inheritance
            comment: '', serialStart: '', serialEnd: '', imeiStart: '', imeiEnd: '',
            deliveryDate: '', shippingDate: '', quantity: '', uom: '', unitPrice: '', amount: '',
            customsCode: '', inspectionDate: ''
        });
        setEditingCharge(null);
        setError('');
    };

    const handleEdit = (charge) => {
        setViewMode(false);
        setEditingCharge(charge);
        setFormData({
            id: charge.charge_id || charge.id, // Display ID
            date: charge.production_date ? charge.production_date.split('T')[0] : '',
            order: charge.order_number || '',
            sw: charge.software_version || '',
            status: charge.status || (statuses.length > 0 ? statuses[0].name : ''),
            comment: charge.comments || '',
            serialStart: charge.serial_start || '',
            serialEnd: charge.serial_end || '',
            imeiStart: charge.imei_start || '',
            imeiEnd: charge.imei_end || '',
            deliveryDate: charge.delivery_date ? charge.delivery_date.split('T')[0] : '',
            shippingDate: charge.shipping_date ? charge.shipping_date.split('T')[0] : '',
            quantity: charge.quantity || '',
            uom: charge.uom || '',
            unitPrice: charge.unit_price || '',
            amount: charge.amount || '',
            customsCode: charge.customs_code || '',
            inspectionDate: charge.inspection_date ? charge.inspection_date.split('T')[0] : ''
        });
        setIsModalOpen(true);
    };

    const handleView = (charge) => {
        setViewMode(true);
        setEditingCharge(charge);
        setFormData({
            id: charge.charge_id || charge.id,
            date: charge.production_date ? charge.production_date.split('T')[0] : '',
            order: charge.order_number || '',
            sw: charge.software_version || '',
            status: charge.status || '',
            comment: charge.comments || '',
            serialStart: charge.serial_start || '',
            serialEnd: charge.serial_end || '',
            imeiStart: charge.imei_start || '',
            imeiEnd: charge.imei_end || '',
            deliveryDate: charge.delivery_date ? charge.delivery_date.split('T')[0] : '',
            shippingDate: charge.shipping_date ? charge.shipping_date.split('T')[0] : '',
            quantity: charge.quantity || '',
            uom: charge.uom || '',
            unitPrice: charge.unit_price || '',
            amount: charge.amount || '',
            customsCode: charge.customs_code || '',
            inspectionDate: charge.inspection_date ? charge.inspection_date.split('T')[0] : ''
        });
        setIsModalOpen(true);
    };

    const handleDeleteClick = (charge) => {
        setChargeToDelete(charge);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (chargeToDelete) {
            await deleteCharge(chargeToDelete.id);
            setIsDeleteModalOpen(false);
            setChargeToDelete(null);
        }
    };

    const openNewModal = () => {
        resetForm();
        setViewMode(false);
        setIsModalOpen(true);
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Charge Management</h2>
                    <p className="text-xs text-stone-500 mt-1">Production batches and serial numbers.</p>
                </div>
                <div className="flex gap-3">
                    {canEditContent && <GhostButton onClick={openNewModal}><Plus size={16} /> Add Charge</GhostButton>}
                    <GhostButton onClick={() => setIsIMEIModalOpen(true)} title="IMEI-Verwaltung öffnen" aria-label="IMEI-Verwaltung öffnen"><Settings size={16} /> IMEI-Verwaltung</GhostButton>
                    {canEditContent && <GhostButton>Import</GhostButton>}
                    {canEditContent && <GhostButton>Export</GhostButton>}
                </div>
            </div>

            <div className="bg-black/20 border border-white/5 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <SortableHeader label="Charge ID" sortKey="charge_id" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Prod. Date" sortKey="production_date" currentSort={sortConfig} onSort={requestSort} className="text-right" />
                                <SortableHeader label="Order" sortKey="order_number" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Qty" sortKey="quantity" currentSort={sortConfig} onSort={requestSort} className="text-right" />
                                <SortableHeader label="Serial Range" sortKey="serial_start" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={requestSort} />
                                <th className="bg-black/40 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-white/5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedCharges.map((charge, idx) => (
                                <tr
                                    key={idx}
                                    onClick={() => setActiveCharge(charge)}
                                    className={`cursor-pointer transition-colors border-b border-white/5 last:border-0 ${activeCharge?.id === charge.id ? 'bg-stone-800' : 'hover:bg-stone-800/30'}`}
                                >
                                    <td className="px-4 py-0.5 text-white font-medium">{charge.charge_id || charge.id}</td>
                                    <td className="px-4 py-0.5 text-right text-stone-400">{charge.production_date ? new Date(charge.production_date).toLocaleDateString() : '-'}</td>
                                    <td className="px-4 py-0.5 text-stone-300">{charge.order_number || charge.order || '-'}</td>
                                    <td className="px-4 py-0.5 text-right text-stone-300">{charge.quantity || '-'} <span className="text-stone-500 text-xs">{charge.uom}</span></td>
                                    <td className="px-4 py-0.5 text-stone-400 text-xs">
                                        {(charge.serial_start || charge.serialStart) && (charge.serial_end || charge.serialEnd)
                                            ? `${charge.serial_start || charge.serialStart} - ${charge.serial_end || charge.serialEnd}`
                                            : (charge.serial_start || charge.serialStart || charge.serial_end || charge.serialEnd || '-')}
                                    </td>
                                    <td className="px-4 py-0.5">
                                        <span
                                            className="px-2 py-1 rounded text-xs border text-white"
                                            style={{
                                                borderColor: statuses.find(s => s.name === charge.status)?.hex_code || '#444',
                                                backgroundColor: statuses.find(s => s.name === charge.status)?.hex_code ? `${statuses.find(s => s.name === charge.status)?.hex_code}20` : 'transparent'
                                            }}
                                        >
                                            {charge.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-0.5 text-right">
                                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => handleView(charge)}
                                                className="w-8 h-8 rounded border border-stone-600 text-stone-400 hover:text-stone-200 hover:border-stone-500 flex items-center justify-center transition"
                                                title="View Details"
                                            >
                                                <Eye size={14} />
                                            </button>
                                            {canEditContent && (
                                                <>
                                                    <button
                                                        onClick={() => handleEdit(charge)}
                                                        className="w-8 h-8 rounded border border-stone-600 text-stone-400 hover:text-blue-400 hover:border-blue-900/50 flex items-center justify-center transition"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(charge)}
                                                        className="w-8 h-8 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit/Create Modal */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={viewMode ? "View Charge" : (editingCharge ? "Edit Charge" : "New Charge")}>
                <div className="space-y-4">
                    {/* UX Checklist: Parent Info Block & Inheritance Notice */}
                    {!editingCharge && !viewMode && informationalParent && (
                        <div className="space-y-4">
                            {/* Parent Info Block */}
                            <div className="bg-black/20 border border-white/5 rounded p-3 text-sm">
                                <div className="text-xs uppercase text-stone-500 font-semibold mb-2">Source for Inheritance (Most Recent Charge)</div>
                                <div className="flex justify-between items-center text-stone-300">
                                    <div>
                                        <span className="text-stone-500 mr-2">Charge ID:</span>
                                        <span className="font-mono text-white bg-stone-800 px-1 py-0.5 rounded">{informationalParent.charge_id || informationalParent.id}</span>
                                    </div>
                                    <div>
                                        <span className="text-stone-500 mr-2">Status:</span>
                                        <Badge color={statuses.find(s => s.name === informationalParent.status)?.hex_code || '#444'}>
                                            {informationalParent.status}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            {/* Inheritance Notice */}
                            <div className="bg-blue-900/20 border border-blue-800/50 rounded p-3 flex items-start gap-3">
                                <div className="text-blue-400 mt-0.5">
                                    <AlertTriangle size={18} />
                                </div>
                                <div className="text-sm text-blue-100">
                                    <h4 className="font-semibold mb-1">1:1 Clone-on-Create</h4>
                                    <p className="text-blue-300/80 text-xs">
                                        This new charge will automatically inherit all properties, documents, pictures, and logistics data from the source charge.
                                    </p>
                                    <p className="text-blue-300/80 text-xs mt-1">
                                        <strong>Status:</strong> Will be inherited as <span className="text-blue-200 font-medium">{informationalParent.status}</span> unless you select a different status below.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-900/20 border border-red-800 text-red-400 p-3 rounded flex items-center gap-2">
                            <AlertTriangle size={16} />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {/* 1. Identifikation & Bestellung */}
                    <div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Charge ID (Batch No.)</label>
                                <Input name="id" value={formData.id} onChange={handleChange} placeholder="e.g. BATCH001" disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Order Number</label>
                                <Input name="order" value={formData.order} onChange={handleChange} placeholder="e.g. PO-12345" disabled={viewMode} />
                            </div>
                        </div>
                    </div>

                    {/* 2. Zeitlicher Ablauf (chronologisch) */}
                    <div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Production Date</label>
                                <Input name="date" type="date" value={formData.date} onChange={handleChange} disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Inspection Date</label>
                                <Input name="inspectionDate" type="date" value={formData.inspectionDate} onChange={handleChange} disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Shipping Date</label>
                                <Input name="shippingDate" type="date" value={formData.shippingDate} onChange={handleChange} disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Delivery Date</label>
                                <Input name="deliveryDate" type="date" value={formData.deliveryDate} onChange={handleChange} disabled={viewMode} />
                            </div>
                        </div>
                    </div>

                    {/* 3. Mengen, Einheiten & Preise */}
                    <div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Quantity</label>
                                <Input name="quantity" type="number" value={formData.quantity} onChange={handleChange} placeholder="e.g. 100" disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Unit of Measure</label>
                                <Input name="uom" value={formData.uom} onChange={handleChange} placeholder="e.g. pcs, kg" disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Unit Price</label>
                                <Input name="unitPrice" type="number" step="0.01" value={formData.unitPrice} onChange={handleChange} placeholder="0.00" disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Amount</label>
                                <Input name="amount" type="number" step="0.01" value={formData.amount} onChange={handleChange} placeholder="0.00" disabled={viewMode} />
                            </div>
                        </div>
                    </div>

                    {/* 4. Seriennummern-Bereich */}
                    <div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Serial Start</label>
                                <Input name="serialStart" value={formData.serialStart} onChange={handleChange} placeholder="Start Serial" disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Serial End</label>
                                <Input name="serialEnd" value={formData.serialEnd} onChange={handleChange} placeholder="End Serial" disabled={viewMode} />
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-medium text-stone-400 uppercase">IMEI Start</label>
                                    <button
                                        type="button"
                                        onClick={() => setIsIMEIModalOpen(true)}
                                        title="IMEI-Verwaltung öffnen"
                                        aria-label="IMEI-Verwaltung öffnen"
                                        className="text-stone-400 hover:text-blue-400 hover:bg-stone-800 px-1.5 py-0.5 rounded transition flex items-center gap-1 text-[11px] border border-white/5"
                                    >
                                        <Settings size={13} className="hover:rotate-45 transition-transform" />
                                        <span>IMEI-Pool</span>
                                    </button>
                                </div>
                                <Input name="imeiStart" value={formData.imeiStart} onChange={handleChange} placeholder="14-stellige IMEI Basis" disabled={viewMode} />
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-medium text-stone-400 uppercase">IMEI End</label>
                                    <span className="text-[10px] text-stone-500 font-mono">Letzte enthaltene Basis</span>
                                </div>
                                <Input name="imeiEnd" value={formData.imeiEnd} onChange={handleChange} placeholder="14-stellige IMEI Basis" disabled={viewMode} />
                            </div>
                        </div>
                    </div>

                    {/* 5. Technische Details */}
                    <div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Version</label>
                                <Input name="sw" value={formData.sw} onChange={handleChange} placeholder="e.g. v1.0.2" disabled={viewMode} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Customs Code</label>
                                <Input name="customsCode" type="number" value={formData.customsCode} onChange={handleChange} placeholder="e.g. 123456" disabled={viewMode} />
                            </div>
                        </div>
                    </div>

                    {/* 6. Status & Notizen */}
                    <div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Status</label>
                                <div className="flex gap-2">
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        disabled={viewMode}
                                        style={{
                                            borderColor: statuses.find(s => s.name === formData.status)?.hex_code || '',
                                            backgroundColor: statuses.find(s => s.name === formData.status)?.hex_code ? `${statuses.find(s => s.name === formData.status)?.hex_code}20` : ''
                                        }}
                                        className={`w-full bg-black/20 border border-white/5 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500 appearance-none transition-colors ${viewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {/* Status Inheritance Logic */}
                                        {!editingCharge && !viewMode && informationalParent && (
                                            <option value="" style={{ backgroundColor: '#0c0a09', color: '#60a5fa' }}>(Inherit: {informationalParent.status})</option>
                                        )}
                                        {/* Classic Default for Editing or No Parent */}
                                        {!informationalParent && <option value="" style={{ backgroundColor: '#0c0a09' }}>Select Status</option>}

                                        {statuses.map(status => (
                                            <option key={status.id} value={status.name} style={{ backgroundColor: '#0c0a09' }}>{status.name}</option>
                                        ))}
                                    </select>
                                    {!viewMode && (
                                        <button className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition">
                                            <Settings size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-stone-400 mb-1 uppercase">Comments</label>
                                <Input name="comment" value={formData.comment} onChange={handleChange} placeholder="Optional comments..." disabled={viewMode} />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/5">
                        <GhostButton onClick={() => setIsModalOpen(false)}>{viewMode ? 'Close' : 'Cancel'}</GhostButton>
                        {!viewMode && <PrimaryButton onClick={handleSubmit}>{editingCharge ? 'Update Charge' : 'Create Charge'}</PrimaryButton>}
                    </div>
                </div>
            </Modal>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Charge"
                message={`Are you sure you want to delete charge "${chargeToDelete?.charge_id || chargeToDelete?.id}"? This action cannot be undone.`}
            />

            {/* IMEI Management Modal */}
            <IMEIManagementModal
                isOpen={isIMEIModalOpen}
                onClose={() => setIsIMEIModalOpen(false)}
                modelName={productData?.model_name || productData?.model}
                sku={productData?.sku}
                orderNumber={formData.order || editingCharge?.order_number || activeCharge?.order_number}
                chargeId={editingCharge?.id || (isModalOpen ? null : activeCharge?.id)}
                batchNumber={formData.id || editingCharge?.batch_number || activeCharge?.batch_number}
                deviceQuantity={formData.quantity || editingCharge?.quantity || activeCharge?.quantity}
                onAllocationConfirmed={(start, end) => {
                    setFormData(prev => ({ ...prev, imeiStart: start, imeiEnd: end }));
                }}
            />
        </div>
    );
};
